<template lang="pug">
.license-warning-banner(
    v-if="showBanner"
    :class="blocking ? 'license-warning-banner--blocking' : 'license-warning-banner--warning'"
    :style="bannerStyle"
    role="alert"
)
    img.license-warning-banner__icon(:src="iconSrc" alt="" aria-hidden="true")
    span.license-warning-banner__prefix(v-if="prefix") {{ prefix }}
    span.license-warning-banner__text {{ message }}
</template>

<script>
/**
 * 授權警告橫幅。Portal Chat 與 Work Agent 兩個頁面共用，掛在 content 區最上緣。
 *
 * 訊息組裝與日期格式化只在這裡做一次，兩個頁面都不得各自實作。
 *
 * 兩種變體，分色依據是「這個狀態會不會擋住送出／執行」：
 *   阻擋（SUSPENDED / INVALID）  紅底、粗體前綴、**沒有關閉鈕**——功能已經不能用，
 *                                訊息關掉使用者就不知道為什麼送不出去。
 *   警告（EXPIRING_SOON /        橘底、無前綴——功能照常，只是提醒該續約了。
 *        EXPIRED_GRACE）
 * 兩種都不給關閉：需求要求使用者無法把它關掉。
 * 樣式與 UpAgent 桌面端的橫幅一致（同一份設計規範）。
 *
 * 狀態來自 Vuex，由頁面自行 dispatch('fetchLicenseStatus') 取得；
 * 本元件不主動查詢，避免兩個頁面各掛一份時重複打 API。
 *
 * 版面上有兩件事由本元件在執行時「量出來」，不寫死也不要求掛載點配合：
 *
 *   1. 左右貼齊：掛載點到 content 區邊界之間可能疊了**好幾層**內距（Work Agent
 *      就是 px-md-2 的 8px 再加上 .DocumentAgent 的 12px），各層的值還會隨斷點變。
 *      所以往上累加每一層的水平內距，一路加到 content 區的邊界為止，再用負邊距
 *      整體抵掉。只抵掉直接父層是不夠的——那是這支元件前幾版一直有殘留留白的原因。
 *   2. 上方位置：目標是「固定頂欄的正下方」。窄視窗時 MenuBarPortal 從常駐側邊欄
 *      換成 position:fixed 的 v-app-bar（兩者互斥）會蓋住 content 區最上緣，直接量
 *      那顆元素的實際高度，不複製它的斷點條件——那邊改了這裡才不會漂掉。
 *      上邊距 = 頂欄高度 - 累加的上內距；沒有頂欄時為負值，剛好把上內距抵掉。
 *
 * 兩者都在掛載、視窗改變大小、以及橫幅由隱藏轉為顯示時重新量。
 */

// i18n 的 locale 值（zh_TW 等）對應到 toLocaleDateString 用的 BCP47 標籤。
const DATE_LOCALES = {
    zh_TW: 'zh-TW',
    zh_CN: 'zh-CN',
    en_US: 'en-US',
    ja_JP: 'ja-JP',
};

// 狀態圖示與底色（與 UpAgent 桌面端同一份設計規範、同兩張 SVG）：
//   Failed  icon #ED4242 / bg red-100 #FCEDED / border red-200 #F6C2C2
//   Warning icon #EA7A2A / bg #FDF3EA / border #F6D6B8
//           （bg 與 border 是 alert 專用值，色階中沒有對應的淺橘）
// 顏色烙在 SVG 裡，不由外部覆寫，兩個產品才不會各自調色調到不一樣。
const ICON_BLOCKING = require('@/assets/images/license_status_fail.svg');
const ICON_WARNING = require('@/assets/images/license_status_warning.svg');

// 阻擋變體才有的粗體前綴。
const PREFIX_KEYS = {
    SUSPENDED: 'LICENSE_WARNING.PREFIX_SUSPENDED',
    INVALID: 'LICENSE_WARNING.PREFIX_INVALID',
};

// MenuBarPortal 在窄視窗時渲染的固定頂欄。存在就要讓開它的高度。
const APP_BAR_ID = 'menu-bar-portal-mobile';

// 往上累加內距的終點：這幾個是 content 區的最外層容器，不再往上抵。
// 它們本身的內距（目前是 0）也算進去，日後若加了內距同樣會被抵掉。
const CONTENT_ROOT_SELECTOR = '.portal-container, .main-page, body';

// 保險上限，避免 DOM 結構異常時一路走到 document。
const MAX_ANCESTOR_WALK = 8;

export default {
    data() {
        return {
            parentPadLeft: 0,
            parentPadRight: 0,
            parentPadTop: 0,
            appBarHeight: 0,
        };
    },
    computed: {
        bannerStyle() {
            const style = {};
            // 負邊距抵掉父容器的水平內距，讓 bar 貼齊 content 區邊緣、整條橫跨
            // （設計要求 radius 0、只留下邊框）。
            if (this.parentPadLeft) {
                style.marginLeft = `-${this.parentPadLeft}px`;
            }
            if (this.parentPadRight) {
                style.marginRight = `-${this.parentPadRight}px`;
            }
            // 上方要落在「固定頂欄的正下方」：橫幅自然位置是距 content 區頂端
            // parentPadTop，目標是 appBarHeight（沒有頂欄時就是 0），差額即為
            // 需要的上邊距。頂欄不存在時這個值是負的，剛好把上層的內距抵掉。
            const topOffset = this.appBarHeight - this.parentPadTop;
            if (topOffset) {
                style.marginTop = `${topOffset}px`;
            }
            return style;
        },
        iconSrc() {
            return this.blocking ? ICON_BLOCKING : ICON_WARNING;
        },
        statusCode() {
            return this.$store.state.licenseStatusCode;
        },
        blocking() {
            return this.$store.getters.isLicenseBlocking;
        },
        showBanner() {
            return this.$store.getters.hasLicenseWarning;
        },
        prefix() {
            const key = this.blocking ? PREFIX_KEYS[this.statusCode] : null;
            return key ? this.$t(key) : '';
        },
        message() {
            if (!this.showBanner) {
                return '';
            }
            // 四個狀態碼與 LICENSE_WARNING 的四個 key 同名，直接對應。
            // INVALID 沒有可信的日期，帶空字串進去也不會被文案用到。
            return this.$t(`LICENSE_WARNING.${this.statusCode}`, {
                date: this.formatDate(this.$store.state.licenseExpiryDate),
                stopDate: this.formatDate(this.$store.state.licenseStopDate),
            });
        },
    },
    watch: {
        // 由隱藏轉為顯示時 $el 才存在，這時才量得到。
        showBanner(shown) {
            if (shown) {
                this.$nextTick(this.measureLayout);
            }
        },
    },
    mounted() {
        this.$nextTick(this.measureLayout);
        window.addEventListener('resize', this.onWindowResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
        if (this._resizeRaf) {
            window.cancelAnimationFrame(this._resizeRaf);
        }
    },
    methods: {
        onWindowResize() {
            // resize 期間會連續觸發，收斂到每一影格量一次。
            if (this._resizeRaf) {
                window.cancelAnimationFrame(this._resizeRaf);
            }
            this._resizeRaf = window.requestAnimationFrame(() => {
                this._resizeRaf = null;
                this.measureLayout();
            });
        },
        measureLayout() {
            if (this.$el) {
                // 從直接父層往上累加水平內距，直到 content 區的最外層容器。
                // 中間任何一層有內距都會把橫幅往內縮，所以要整體抵掉，
                // 只看直接父層會留下上層的縮排。
                let left = 0;
                let right = 0;
                let top = 0;
                let el = this.$el.parentElement;
                for (let i = 0; el && i < MAX_ANCESTOR_WALK; i += 1) {
                    const cs = window.getComputedStyle(el);
                    left += parseFloat(cs.paddingLeft) || 0;
                    right += parseFloat(cs.paddingRight) || 0;
                    top += parseFloat(cs.paddingTop) || 0;
                    if (el.matches(CONTENT_ROOT_SELECTOR)) {
                        break;
                    }
                    el = el.parentElement;
                }
                this.parentPadLeft = left;
                this.parentPadRight = right;
                this.parentPadTop = top;
            }
            // 固定頂欄不存在（常駐側邊欄版面）時高度為 0，不需要讓開。
            const appBar = document.getElementById(APP_BAR_ID);
            const rect = appBar ? appBar.getBoundingClientRect() : null;
            this.appBarHeight = rect && rect.height > 0 ? rect.height : 0;
        },
        formatDate(isoString) {
            if (!isoString) {
                return '';
            }
            const parsed = new Date(isoString);
            if (isNaN(parsed.getTime())) {
                return '';
            }
            const dateLocale = DATE_LOCALES[this.$i18n.locale] || DATE_LOCALES.zh_TW;
            // 只到年月日、不顯示時間。
            //
            // timeZone 固定 UTC，不換算瀏覽器時區。發卡工具把 `--expiry 2027-12-31`
            // 正規化成 `2027-12-31T23:59:59Z`，換算到 UTC+8 就變成隔天 07:59，
            // 畫面會顯示 2028/01/01——比合約上的到期日晚一天。我方客戶全在正時區，
            // 等於全部都會看到晚一天。固定 UTC 顯示的就是授權上簽的那個日期。
            // UpAgent 端的橫幅也必須用同一個做法，否則兩邊的日期會不一致。
            return parsed.toLocaleDateString(dateLocale, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'UTC',
            });
        },
    },
};
</script>

<style scoped>
/* content 區最上緣的橫幅：radius 0、只留下邊框、單行置中對齊。 */
.license-warning-banner {
    /* flex: 0 0 auto 是必要的，不是保險：掛載點（Work Agent 頁）是
       d-flex.flex-column 且 min-height:0，預設的 flex-shrink:1 會在內容溢出時
       把橫幅壓成 0 高度——視窗越窄壓得越徹底。旁邊那個 v-row 也是為此釘了
       flex: 0 0 auto。 */
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    border-radius: 0;
    border-bottom: 1px solid transparent;
    font-size: 13px;
    line-height: 20px;
    color: #444444; /* fg-1 */
}

/* Failed：bg red-100 / border red-200 */
.license-warning-banner--blocking {
    background: #FCEDED;
    border-bottom-color: #F6C2C2;
}

/* Warning：底色與邊框為 alert 專用值 */
.license-warning-banner--warning {
    background: #FDF3EA;
    border-bottom-color: #F6D6B8;
}

.license-warning-banner__icon {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
}

.license-warning-banner__prefix {
    flex: 0 0 auto;
    font-weight: 700;
}

.license-warning-banner__text {
    flex: 1 1 auto;
}

</style>
